/**
 * Fold a string to something a search box can match.
 *
 * Polish is the source language, so a trainer types "sila" and expects "Siła". NFD decomposition plus
 * stripping combining marks handles ą ć ę ń ó ś ź ż, but **not `ł`** — U+0142 is a precomposed letter
 * with a stroke, not a base plus a mark, so it survives NFD untouched and "sila" would miss "Siła".
 * The two stroke letters are therefore mapped by hand.
 *
 * ── It is a MODULE of its own because the fold happens at two different times ────────────────
 * Once per ROW, in the projection, where it is paid for on the response rather than on the
 * keystroke; and once per TERM, in the filter, on every keystroke. Both readings have to be the
 * same reading or a folded row would stop matching a folded term — one function, three callers,
 * and no way for the two to drift. Folding the rows in the filter instead cost 120 rows × 2 fields
 * of NFD normalisation plus three regex passes each, for every character typed.
 *
 * The same rule `pages/clients` uses, deliberately re-derived rather than imported: that slice is
 * frozen and nothing may be promoted out of it. When it is replaced, this is the copy that moves to
 * `shared/lib` — two real consumers by then.
 *
 * Pure. No React, no DOM, no i18n, no query hook.
 */

export function foldForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .toLowerCase();
}

/**
 * The separator between the fields a row is searchable by.
 *
 * A row's `searchText` is its fields folded and joined, and `includes` over a joined string would
 * otherwise match across the seam — a plan named `Sił` beside a category `a` would answer to
 * `siła`. A newline cannot be typed into a single-line search field, so no term can span it.
 */
const FIELD_SEPARATOR = '\n';

/**
 * Everything one row can be found by, folded once.
 *
 * Called by the two projections, never by the filter: the whole point is that a row is folded when
 * its response lands and a term is folded when a key is pressed.
 */
export function searchTextOf(...fields: readonly (string | null)[]): string {
  return foldForSearch(fields.map((field) => field ?? '').join(FIELD_SEPARATOR));
}
